import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';

// components
import { Card, Modal } from '@mui/material';
import { toast } from 'sonner';
import VoterLookupSearchScreen from './VoterLookupSearchScreen';
import { VoterProfileType, VoterSearchForm } from 'src/@types/voterSearch';
import { useVoterSearch } from 'src/hooks/query/voterSearch';
import { useOrg } from 'src/hooks/query/orgHooks';
import VoterLookupLoadingScreen from './VoterLookupLoadingScreen';
import VoterLookupResultsScreen from './VoterLookupResultsScreen';
import VoterProfileSkeleton from 'src/components/voter-profile/VoterProfileSkeleton';
import { useQueryClient } from '@tanstack/react-query';
import { mixpanelTrack } from 'src/utils/analytics';

// Reached from the header on every page, and the profile carries the form components, the
// phone-number libraries and mapbox-gl, none of which a search needs.
const VoterProfile = lazy(() => import('src/components/voter-profile/VoterProfile'));

type VoterLookupModalProps = {
  open: boolean;
  onClose?: () => void;
  /** Merged over the org-state default when building the initial search query. */
  initialSearchValues?: Partial<VoterSearchForm>;
  /** When true and the (prefilled) criteria are non-empty, automatically runs the search on open. */
  autoSearch?: boolean;
  /** Rendered on the 'voter' screen, alongside VoterProfile, so callers can inject actions (e.g. "Link to this contact"). */
  renderVoterActions?: (voter: VoterProfileType) => React.ReactNode;
};

type VoterLookupModalScreen = 'search' | 'loading' | 'results' | 'voter';

function VoterLookupModal({
  open,
  onClose,
  initialSearchValues,
  autoSearch,
  renderVoterActions,
}: VoterLookupModalProps) {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const [currentScreen, setCurrentScreen] = useState<VoterLookupModalScreen>('search');
  const { data: org } = useOrg();
  const [shouldSearch, setShouldSearch] = useState(false);
  const [currentVoter, setCurrentVoter] = useState<VoterProfileType | null>(null);
  const [showMore, setShowMore] = useState(false);

  const getEmptySearchQuery = useCallback(
    () => ({
      first_name: '',
      middle_name: '',
      last_name: '',
      address: '',
      city: '',
      state:
        org?.selected_state && org.selected_state !== 'NA' && org.selected_state !== 'NS'
          ? org.selected_state
          : '',
      zip_code: '',
      phone: '',
      email: '',
      county_name: '',
      precinct_name: '',
      data_source_id: '',
      numinar_id: '',
      jurisdiction_voter_id: '',
      state_voter_id: '',
    }),
    [org?.selected_state]
  );

  const getInitialSearchQuery = useCallback(
    () => ({
      ...getEmptySearchQuery(),
      ...initialSearchValues,
    }),
    [getEmptySearchQuery, initialSearchValues]
  );

  const [searchQuery, setSearchQuery] = useState<VoterSearchForm>(getInitialSearchQuery());

  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (org && !hasInitialized) {
      // A state the caller explicitly supplied (e.g. from a contact's address) is not
      // a default to override, so only apply the org's state when none was given.
      if (!initialSearchValues?.state) {
        setSearchQuery((prev) => ({
          ...prev,
          state:
            org.selected_state && org.selected_state !== 'NA' && org.selected_state !== 'NS'
              ? org.selected_state
              : '',
        }));
      }
      setHasInitialized(true);
    }
  }, [org, hasInitialized, initialSearchValues?.state]);

  useEffect(() => {
    if (open && autoSearch && hasInitialized && !shouldSearch && currentScreen === 'search') {
      const hasCriteria = Object.entries(searchQuery).some(([key, value]) => {
        if (key === 'state') return false;
        return Boolean(value);
      });
      if (hasCriteria) {
        setCurrentScreen('loading');
        setShouldSearch(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoSearch, hasInitialized]);

  const voterSearchQuery = useVoterSearch(
    {
      ...searchQuery,
      limit: 100,
    },
    {
      enabled: shouldSearch,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (!shouldSearch) {
      return;
    }

    if (voterSearchQuery.isSuccess && voterSearchQuery.data) {
      setShouldSearch(false);
      setCurrentScreen('results');
    } else if (voterSearchQuery.isError) {
      setShouldSearch(false);
      setCurrentScreen('search');
      toast.error(
        voterSearchQuery.error?.message || 'Error fetching voter data. Please try again.'
      );
    } else if (voterSearchQuery.isFetching) {
      setCurrentScreen('loading');
    }
  }, [
    voterSearchQuery.isFetching,
    voterSearchQuery.isSuccess,
    voterSearchQuery.isError,
    voterSearchQuery.data,
    voterSearchQuery.error,
    shouldSearch,
  ]);

  useEffect(() => {
    if (open) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    // Reset everything to initial state
    setSearchQuery(getInitialSearchQuery());
    setCurrentScreen('search');
    setShouldSearch(false);
    setCurrentVoter(null);
    setHasInitialized(false);
  };

  const onSearch = (data: VoterSearchForm) => {
    setSearchQuery(data);
    setCurrentScreen('loading');
    mixpanelTrack('Looked Up Voter', {
      org_id: [org?.id],
      search_query: data,
    });
    setShouldSearch(true);
  };

  const onClear = () => {
    setSearchQuery(getEmptySearchQuery());
  };

  const onSearchResultsBack = () => {
    setCurrentScreen('search');
  };

  const onSearchResultsSelect = (voter: VoterProfileType) => {
    setCurrentVoter(voter);
    setCurrentScreen('voter');
  };

  const onVoterViewClose = () => {
    setCurrentVoter(null);
    setCurrentScreen('results');
  };

  const onCancelSearch = () => {
    queryClient.cancelQueries({
      queryKey: ['voterSearch', searchQuery],
    });
    setCurrentScreen('search');
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Card
        sx={{
          width: 700,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'absolute',
          left: '50%',
          top: '10%',
          transform: 'translate(-50%, 0)',
          borderRadius: 2,
          padding: 3,
        }}
      >
        {currentScreen === 'search' && (
          <VoterLookupSearchScreen
            searchValues={searchQuery}
            onSearch={onSearch}
            onClear={onClear}
            showMore={showMore}
            setShowMore={setShowMore}
          />
        )}
        {currentScreen === 'loading' && (
          <VoterLookupLoadingScreen search={searchQuery} onBack={onCancelSearch} />
        )}
        {currentScreen === 'results' && (
          <VoterLookupResultsScreen
            search={searchQuery}
            results={voterSearchQuery.data || []}
            onBack={onSearchResultsBack}
            onVoterClick={onSearchResultsSelect}
          />
        )}
        {currentScreen === 'voter' && currentVoter && (
          <Suspense fallback={<VoterProfileSkeleton />}>
            <VoterProfile
              voter={currentVoter}
              onBack={onVoterViewClose}
              headerAction={renderVoterActions?.(currentVoter)}
            />
          </Suspense>
        )}
      </Card>
    </Modal>
  );
}

export default VoterLookupModal;
